import * as particles from "pixi-particles";
import { AssetConfigSchema } from "slotworks/config/asset-config-schema";
import { ParticleConfig } from "slotworks/config/component-config-schema";
import { AnimatedParticleConfig } from "slotworks/config/component-config-schema";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import DeviceInfo from "slotworks/utils/device-info";
import Point from "slotworks/utils/geom/point";
import Timer from "slotworks/utils/timer";

class ParticleManager {

    // Automatically cap particles above 50 to 50% on mobile
    public autoCap: boolean = true;

    private configs: Map<string, ParticleConfig>;

    private lastTime: number;
    private emitters: Map<number, particles.Emitter> = new Map<number, particles.Emitter>();

    private emitterId: number = 0;

    constructor() {
        this.configs = new Map<string, ParticleConfig>();
    }

    public init(assetConfig: AssetConfigSchema) {
        const particleConfigs = assetConfig.layouts.all.particles;
        Object.keys(particleConfigs).forEach((key: string) => {
            if (particleConfigs.hasOwnProperty(key)) {
                this.configs.set(key, particleConfigs[key]);
            }
        });
    }

    public add(id: string, position: Point, container: PIXI.Container): number {

        this.emitterId++;

        const config = this.configs.get(id);

        const processedTextures = config.textures.map((asset: string | AnimatedParticleConfig): any => {
            if (typeof asset === "string") {
                return GraphicsManager.getTexture(asset);
            } else {
                const processedFrames = asset.textures.map((frame: string) => GraphicsManager.getTexture(frame));
                return {
                    framerate: asset.framerate,
                    loop: asset.loop,
                    textures: processedFrames
                };
            }
        });

        if (DeviceInfo.isPhone && this.autoCap) {
            let cappedParticles = config.config.maxParticles;
            if (cappedParticles > 50) {
                cappedParticles = 50 + (cappedParticles - 50) * 0.5;
            }
            config.config.maxParticles = cappedParticles;
        }

        const emitter = new particles.Emitter(
            container,
            processedTextures,
            config.config
        );

        if (typeof config.textures[0] !== "string") {
            emitter.particleConstructor = particles.AnimatedParticle;
        }

        emitter.parent.x = position.x;
        emitter.parent.y = position.y;

        this.emitters.set(this.emitterId, emitter);
        emitter.addAtBack = false;
        emitter.emit = true;

        return this.emitterId;
    }

    public remove(id: number, instant = true) {
        const emitter = this.emitters.get(id);
        if (emitter) {
            const delay = (instant) ? 0 : emitter.maxLifetime * 1000;

            if (!instant) {
                emitter.spawnChance = 0;
            }

            Timer.setTimeout(() => {
                emitter.emit = false;
                emitter.cleanup();
                emitter.destroy();
                this.emitters.delete(id);
            }, delay);
        }
    }

    public update(time: number) {
        if (this.lastTime) {
            this.emitters.forEach((emitter: particles.Emitter) => {
                emitter.update((time - this.lastTime) * 0.001);
            });
        }

        this.lastTime = time;
    }

    public get(id: number) {
        return this.emitters.get(id);
    }
}

export default new ParticleManager();
